import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Container,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Input from '~/components/Input';
import InputDate from '~/components/InputDate';
import SidebarMesages from '~/components/PersonalCoverage/SidebarMesages';
import { DocumentType } from '~/constants/documentType';
import { useContractLoader } from '~/hooks/useContractLoader';
import useStore, { storeState } from '~/store';
import { getErrorMessage, logErrorWithContext } from '~/utils/errorHandler';
import { reportError } from '~/utils/reportError';
import FormAdditionalDriver, { Additional_drivers } from './FormAdditionalDriver';
import * as styles from './styles';
import { createValidationSchema } from './validationConditional';

function formatPhoneForMask(phone: string): string {
  const digits = (phone || '').replace(/\D/g, '');
  if (digits.length >= 10) {
    const ddd = digits.slice(0, 2);
    const first = digits.slice(2, 7);
    const second = digits.slice(7, 11);
    return `(${ddd}) ${first}-${second}`;
  }
  return phone || '';
}

type Inputs = {
  socialName: string;
  name: string;
  email: string;
  mobile_phone: string;
  document: string;
  document_type?: string;
  birthDate: string;
};

export default function ContractCustomerPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showFormConductor, setShowFormConductor] = useState(false);
  const [loading, setLoading] = useState(false);
  const [useBuyerData, setUseBuyerData] = useState(false);
  const [additional_drivers, setAdditional_drivers] = useState<Array<Additional_drivers>>([]);

  // Hook para carregar dados do contrato
  const { contract_id } = useContractLoader({ stepName: 'step1' });

  const { contractAsyncPostCustomer, quote, insured } = useStore((store) => ({
    contractAsyncPostCustomer: store.contractAsyncPostCustomer,
    quote: store.quote,
    insured: store.insured
  }));

  const {
    handleSubmit,
    control,
    watch,
    trigger,
    formState: { errors, dirtyFields },
    setValue,
    reset
  } = useForm<Inputs>({
    resolver: yupResolver(createValidationSchema(insured)),
    defaultValues: {
      socialName: '',
      name: '',
      mobile_phone: '',
      document: '',
      birthDate: '',
      email: ''
    }
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleChange = () => {
    const isShow = !showFormConductor;
    setShowFormConductor(isShow);
    if (isShow) {
      setAdditional_drivers([
        {
          name: '',
          document: '',
          document_type: 'CPF',
          birthdate: new Date(),
          degree_of_kinship: ''
        }
      ]);
    } else {
      setAdditional_drivers([]);
    }
  };
  const setDriverAdditional = (index: number, driver: Additional_drivers) => {
    setAdditional_drivers((drivers) => {
      drivers[index] = driver;
      return [...drivers];
    });
  };
  const handleMoreDriver = () => {
    setAdditional_drivers((drivers) => {
      const driver_temp = [...drivers];
      driver_temp.push({
        name: '',
        document: '',
        document_type: 'CPF',
        birthdate: new Date(),
        degree_of_kinship: ''
      });
      return driver_temp;
    });
  };

  const opts = { shouldValidate: true, shouldDirty: true };

  const handleChangeUseBuyerData = () => {
    const newState = !useBuyerData;
    setUseBuyerData(newState);

    if (newState) {
      // Sempre preferir dados do contrato (insured) quando existirem (vindos do GET do contrato)
      const hasInsuredData = insured?.name || insured?.document || insured?.email || insured?.mobile_phone;
      if (hasInsuredData) {
        setValue('socialName', insured?.social_name || '', opts);
        setValue('name', insured?.name ?? '', opts);
        setValue('mobile_phone', formatPhoneForMask(insured?.mobile_phone || ''), opts);
        setValue('document', insured?.document ?? '', opts);
        setValue('birthDate', insured?.birthdate ?? '', opts);
        setValue('email', insured?.email ?? '', opts);
        return;
      }
      // Fallback: dados da cotação (quote)
      if (quote?.name || quote?.cpf || quote?.email) {
        setValue('socialName', '', opts);
        setValue('name', quote.name ?? '', opts);
        setValue('mobile_phone', formatPhoneForMask(quote.phoneNumber || ''), opts);
        setValue('document', quote.cpf ?? '', opts);
        setValue('birthDate', quote.birthDate ?? '', opts);
        setValue('email', quote.email ?? '', opts);
        return;
      }
    }

    reset();
  };

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    if (contract_id) {
      setLoading(true);
      try {
        const payload: any = {
          name: typeof data.name === 'string' ? data.name.trim() : data.name,
          social_name: typeof data.socialName === 'string' ? data.socialName.trim() : data.socialName,
          birthDate: data.birthDate,
          email: typeof data.email === 'string' ? data.email.trim() : data.email,
          mobile_phone: data.mobile_phone,
          document: data.document,
          document_type: DocumentType.CPF
        };

        const orgParam = searchParams.get('org');
        await contractAsyncPostCustomer(contract_id, payload, orgParam ?? undefined);
        setLoading(false);

        const contract_hash_id = searchParams.get('cid');
        const source = searchParams.get('source');
        const params = new URLSearchParams();
        if (contract_hash_id) params.set('cid', contract_hash_id);
        if (source) params.set('source', source);
        if (orgParam) params.set('org', orgParam);

        navigate(`/contract/address?${params.toString()}`);
      } catch (error: any) {
        setLoading(false);

        // Log do erro com contexto
        const errorContext = logErrorWithContext(error, 'contract_step1_customer', {
          contractId: contract_id,
          hasNotifications: error.isAxiosError && !!error.response?.data?.notifications
        });

        // Usar o sistema unificado de tratamento de erros
        const errorResponse = getErrorMessage(error);

        storeState.addToast(errorResponse);

        // Log para Rollbar
        reportError(`Erro ao salvar dados do cliente: ${errorResponse.title}`, error, {
          context: 'contract_step1_customer',
          contractId: contract_id,
          errorType: errorContext.errorType,
          originalNotifications: error.isAxiosError ? error.response?.data?.notifications : undefined
        });
      }
    } else {
      storeState.addToast({
        title: 'Não foi possível recuperar o contrato.',
        message: '',
        type: 'warning'
      });
    }
  };
  const removeDriverAdditional = (index: number) => {
    setAdditional_drivers((drivers) => {
      const driver_temp = [...drivers];
      driver_temp.splice(index, 1);
      return driver_temp;
    });
  };

  useEffect(() => {
    watch((_data, { name, type }) => {
      if (name === 'document' && type === 'change') {
        trigger('document');
      }
    });
  }, [watch]);

  return (
    <Container maxWidth="lg" sx={styles.container}>
      <Grid container spacing={2}>
        <Grid item md={8} xs={12}>
          <Card>
            <CardContent>
              <Box sx={styles.headerContainer}>
                <Typography variant="h6" align="center">
                  Dados para contrato
                </Typography>
              </Box>
              <Divider variant="middle" />
              <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={styles.containerForm}>
                  <Typography sx={{ fontSize: { xs: 16, sm: 20 } }} variant="h6" fontWeight="bold">
                    1 - Informações do segurado
                  </Typography>
                  <FormGroup>
                    <FormControlLabel
                      sx={{
                        marginTop: 1
                      }}
                      control={<Checkbox checked={useBuyerData} onChange={handleChangeUseBuyerData} color="success" />}
                      label={
                        <Typography component={'span'} sx={{ fontSize: { xs: 14, sm: 16 } }}>
                          Utilizar as mesmas informações do “Dados de Comprador”
                        </Typography>
                      }
                    />
                  </FormGroup>
                  <Controller
                    name="socialName"
                    control={control}
                    render={({ field }) => (
                      <>
                        <Input
                          label="Nome Social"
                          placeholder="Seu nome social"
                          error={errors?.socialName ? true : false}
                          isDirty={dirtyFields.socialName}
                          helperText={errors?.socialName?.message}
                          {...field}
                        />
                      </>
                    )}
                  />
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <>
                        <Input
                          label="Nome Completo"
                          placeholder="Seu Nome Completo"
                          error={errors?.name ? true : false}
                          isDirty={dirtyFields.name}
                          helperText={errors?.name?.message}
                          {...field}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          O nome civil informado será utilizado apenas para fins contratuais e fiscais. Para melhor
                          atender às suas necessidades, nos comunique o seu nome social!
                        </Typography>
                      </>
                    )}
                  />
                  <Controller
                    name="mobile_phone"
                    control={control}
                    render={({ field }) => (
                      <>
                        <Input
                          label="Celular"
                          mask="phone"
                          placeholder="(XX) X XXXX-XXXX"
                          error={errors?.mobile_phone ? true : false}
                          isDirty={dirtyFields.mobile_phone}
                          helperText={errors?.mobile_phone?.message}
                          {...field}
                        />
                      </>
                    )}
                  />
                  <Controller
                    name="document"
                    control={control}
                    render={({ field }) => (
                      <>
                        <Input
                          label="CPF"
                          placeholder="XXX.XXX.XXX-XX"
                          mask="cpf"
                          error={errors?.document ? true : false}
                          isDirty={dirtyFields.document}
                          helperText={errors?.document?.message}
                          {...field}
                        />
                      </>
                    )}
                  />
                  <Controller
                    name="birthDate"
                    control={control}
                    render={({ field }) => (
                      <>
                        <InputDate
                          label="Data de nascimento"
                          placeholder="XX/XX/XXXX"
                          error={errors?.birthDate ? true : false}
                          isDirty={dirtyFields.birthDate}
                          helperText={errors?.birthDate?.message}
                          defaultValue={useBuyerData ? new Date(insured?.birthdate) : undefined}
                          setField={(value) => {
                            setValue('birthDate', value);
                          }}
                          {...field}
                        />
                      </>
                    )}
                  />
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <>
                        <Input
                          label="E-mail"
                          placeholder="seuemail@email.com"
                          error={errors?.email ? true : false}
                          isDirty={dirtyFields.email}
                          helperText={errors?.email?.message}
                          {...field}
                        />
                      </>
                    )}
                  />
                  {/* <FormGroup sx={styles.switchContainer}>
                    <FormControlLabel
                      control={
                        <IOSSwitch sx={{ m: 1 }} onChange={handleChange} />
                      }
                      label="Quero incluir um condutor adicional."
                    />
                  </FormGroup> */}
                  {showFormConductor && (
                    <>
                      {additional_drivers.map((driver, index) => (
                        <span key={index}>
                          <FormAdditionalDriver
                            index={index}
                            driver={driver}
                            setDriver={setDriverAdditional}
                            removeDriver={removeDriverAdditional}
                          />
                        </span>
                      ))}
                      <Typography sx={styles.moreDriversStyles} onClick={handleMoreDriver}>
                        + Clique para adicionar mais condutor adicional
                      </Typography>
                    </>
                  )}
                </Box>
                <Box sx={styles.containerButton}>
                  <LoadingButton sx={{ fontSize: { xs: 16, sm: 20 } }} loading={loading} type="submit">
                    Salvar e sair
                  </LoadingButton>
                  <LoadingButton
                    data-testid="contract-step1-next"
                    sx={{ fontSize: { xs: 16, sm: 20 } }}
                    loading={loading}
                    variant="contained"
                    type="submit"
                  >
                    Próximo passo
                  </LoadingButton>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          item
          md={4}
          xs={12}
          sx={{
            display: {
              xs: 'none',
              lg: 'block'
            }
          }}
        >
          <Card>
            <CardContent>
              <SidebarMesages showProduct={true} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
